import {
  type CSSProperties,
  type FC,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useResetRecoilState } from 'recoil'

import { itemAtomFamily } from '@/atoms/_editorAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import { useDuplicateItem } from '@/hooks/editor/useDuplicateItem'
import { useEditorDuplicateTextBox } from '@/hooks/editor/useEditorDuplicateTextBox'
import { useEditorTextBoxDelete } from '@/hooks/editor/useEditorTextBoxDelete'
import { isEditorTextBoxItemType } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import {
  DeleteIcon,
  DragHandleIcon,
  DuplicateIcon,
  LockIcon,
  ResizeCornerDot,
  ResizeHitArea,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import {
  RESIZE_CORNERS,
  RESIZE_HOTSPOT_BLEED_PX,
  resizeHotspotGeometry,
  type ResizeCorner,
} from '@/utils/editor/resizeHotspot'

/**
 * Creates a synthetic mouse event for forwarding to underlying elements.
 * Used by drag and resize controls to forward events to the actual item handlers.
 */
export const createSyntheticMouseEvent = (e: MouseEvent) =>
  new MouseEvent('mousedown', {
    bubbles: true,
    cancelable: true,
    clientX: e.clientX,
    clientY: e.clientY,
    screenX: e.screenX,
    screenY: e.screenY,
    button: e.button,
    buttons: e.buttons,
    ctrlKey: e.ctrlKey,
    shiftKey: e.shiftKey,
    altKey: e.altKey,
    metaKey: e.metaKey,
  })

const TAP_SLOP_PX = 5

/**
 * Fires the action on release rather than on click. The press deliberately
 * skips preventDefault — its focus exit is what commits and ends an open
 * editor text box session — and pointer capture keeps the release on this
 * control even though that same exit can re-dock the icon bar mid-press.
 */
export const useTapAction = (action: () => void) => {
  const pressRef = useRef<{ pointerId: number; x: number; y: number } | null>(
    null,
  )
  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    pressRef.current = { pointerId: e.pointerId, x: e.clientX, y: e.clientY }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // setPointerCapture throws for a pointer that has already released;
      // without capture the release simply hit-tests.
    }
  }, [])
  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const press = pressRef.current
      pressRef.current = null
      if (!press || press.pointerId !== e.pointerId) return
      if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > TAP_SLOP_PX)
        return
      action()
    },
    [action],
  )
  const onPointerCancel = useCallback(() => {
    pressRef.current = null
  }, [])
  return { onPointerDown, onPointerUp, onPointerCancel }
}

const noop = () => {}

/**
 * The lock icon, always visible and leftmost in the toolbar. It shows the
 * item's lock; with `onFlip` set (an editor text box, while
 * `EDITOR_TEXT_BOX_LOCK_FLIPS_IN_TOOLBAR` is on) a tap on it flips the lock.
 * The icon carries no `data-overlay-action`, so the toolbar's mousedown
 * guard keeps an open edit session alive across the flip.
 */
export const CommonControls: FC<{
  itemsLocked: boolean
  onFlip?: () => void
}> = ({ itemsLocked, onFlip }) => {
  const { t } = useTranslation()
  const tap = useTapAction(onFlip ?? noop)
  const label = itemsLocked
    ? t('itemControls.locked')
    : t('itemControls.unlocked')

  return (
    <Tooltip text={label}>
      <LockIcon
        {...(onFlip ? tap : {})}
        $flips={onFlip !== undefined}
        icon={itemsLocked ? 'lockClosedIcon' : 'lockOpenIcon'}
        aria-label={label}
        data-locked={itemsLocked ? 'true' : 'false'}
        data-testid="overlay-lock-icon"
      />
    </Tooltip>
  )
}

/**
 * Drag handle - forwards mousedown to the item for dragging
 */
export const DragControls: FC<{ itemId: string; itemsLocked: boolean }> = ({
  itemId,
  itemsLocked,
}) => {
  const { t } = useTranslation()

  const handleDragMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const itemElement = document.querySelector(
        `[data-item-id="${itemId}"][data-item-selected="true"]`,
      )
      if (!itemElement) return
      itemElement.dispatchEvent(createSyntheticMouseEvent(e))
    },
    [itemId],
  )

  if (itemsLocked) return null

  return (
    <Tooltip text={t('itemControls.move')}>
      <DragHandleIcon
        onMouseDown={handleDragMouseDown}
        icon="bars2Icon"
        aria-label={t('itemControls.move')}
        data-testid="overlay-drag-handle"
      />
    </Tooltip>
  )
}

export const DuplicateControls: FC<{
  itemId: string
  type?: string
  itemsLocked: boolean
}> = ({ itemId, type, itemsLocked }) => {
  const { t } = useTranslation()
  const duplicateItem = useDuplicateItem()
  const duplicateEditorTextBox = useEditorDuplicateTextBox()
  const isEditorTextBoxItem = isEditorTextBoxItemType(type)
  const tap = useTapAction(
    useCallback(() => {
      if (isEditorTextBoxItem) void duplicateEditorTextBox(itemId)
      else duplicateItem()
    }, [isEditorTextBoxItem, duplicateEditorTextBox, duplicateItem, itemId]),
  )

  if (itemsLocked) return null

  return (
    <Tooltip text={t('itemControls.duplicate')}>
      <DuplicateIcon
        {...tap}
        data-overlay-action="duplicate"
        icon="duplicateOutlineIcon"
        aria-label={t('itemControls.duplicate')}
        data-testid="overlay-duplicate-icon"
      />
    </Tooltip>
  )
}

/**
 * Delete button - rightmost in toolbar
 * Always visible regardless of lock state - users can remove items even when locked
 */
export const DeleteControls: FC<{ itemId: string; itemsLocked: boolean }> = ({
  itemId,
}) => {
  const { t } = useTranslation()
  const deleteItem = useResetRecoilState(itemAtomFamily(itemId))
  const resetControlState = useResetRecoilState(
    itemControlStateAtomFamily(itemId),
  )
  const deleteEditorTextBox = useEditorTextBoxDelete()

  const handleDelete = useCallback(() => {
    void deleteEditorTextBox(itemId)
    resetControlState()
    deleteItem()
  }, [deleteEditorTextBox, itemId, deleteItem, resetControlState])
  const tap = useTapAction(handleDelete)

  return (
    <Tooltip text={t('itemControls.delete')}>
      <DeleteIcon
        {...tap}
        data-overlay-action="delete"
        icon="trashOutlineIcon"
        aria-label={t('itemControls.delete')}
        data-testid="overlay-delete-icon"
      />
    </Tooltip>
  )
}

/** The bottom-right id differs by design — the resize e2e specs target it. */
const CORNER_TESTIDS: Record<ResizeCorner, string> = {
  nw: 'overlay-resize-corner-nw',
  ne: 'overlay-resize-corner-ne',
  sw: 'overlay-resize-corner-sw',
  se: 'overlay-resize-wedge',
}

/**
 * Lifts the corner chrome above the ControlToolbar (1) and the hover-raised
 * HideInPdfBadge (2): among `z-index: auto` siblings the later-painted badge
 * wins the hit test and would swallow a corner grab.
 */
export const RESIZE_CHROME_Z = 3

/**
 * The edge pills sit above the corner chrome. A corner's hotspot is an
 * unpainted region that keeps its screen size at every zoom, so on a short
 * box at a low zoom the two hotspots of one side meet over the side pill's
 * centre; a press on the pill then started a corner scale, which moves the
 * height a width drag must leave alone. Where a pill and a hotspot overlap
 * the pill is what is painted under the pointer, so the pill wins.
 */
export const EDGE_PILL_Z = RESIZE_CHROME_Z + 1

const cornerWrapperStyle = (
  corner: ResizeCorner,
  scale: number,
): CSSProperties => {
  const vertical = corner.includes('n') ? 'top' : 'bottom'
  const horizontal = corner.includes('w') ? 'left' : 'right'
  return {
    position: 'absolute',
    [vertical]: `-${RESIZE_HOTSPOT_BLEED_PX}px`,
    [horizontal]: `-${RESIZE_HOTSPOT_BLEED_PX}px`,
    pointerEvents: 'none',
    zIndex: RESIZE_CHROME_Z,
    transform: `scale(${scale})`,
    transformOrigin: `${vertical} ${horizontal}`,
  }
}

/** The four corner grabs of an editor text box, absent while it is locked. */
export const ResizeCornerControls: FC<{
  itemId: string
  itemsLocked: boolean
  zoom: number
  width: number
  height: number
}> = ({ itemId, itemsLocked, zoom, width, height }) => {
  const { t } = useTranslation()

  const handleResizeMouseDown = useCallback(
    (corner: ResizeCorner) => (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const resizeHandle = document.querySelector(
        `[data-resize-handle="${itemId}"][data-resize-placement="${corner}"]`,
      )
      if (!resizeHandle) return
      resizeHandle.dispatchEvent(createSyntheticMouseEvent(e))
    },
    [itemId],
  )

  const handleCornerDoubleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      const boxEl = document.querySelector(
        `[data-item-id="${itemId}"] [data-editor-text-box]`,
      )
      boxEl?.dispatchEvent(
        new MouseEvent('dblclick', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY,
        }),
      )
    },
    [itemId],
  )

  if (itemsLocked) return null

  const scale = Math.min(2, Math.max(0.5, 100 / zoom))

  return (
    <>
      {RESIZE_CORNERS.map((corner) => {
        const hotspot = resizeHotspotGeometry(width, height, corner)
        return (
          <div key={corner} style={cornerWrapperStyle(corner, scale)}>
            <Tooltip
              text={t('itemControls.resize')}
              placement={corner.includes('w') ? 'left' : 'right'}
              offsetX={8}
              padding="10px 6px"
            >
              <ResizeHitArea
                $cursor={corner}
                $width={hotspot.width}
                $height={hotspot.height}
                $clipPath={hotspot.clipPath}
                onMouseDown={handleResizeMouseDown(corner)}
                onDoubleClick={handleCornerDoubleClick}
                aria-label={t('itemControls.resize')}
                data-testid={CORNER_TESTIDS[corner]}
              />
            </Tooltip>
            <ResizeCornerDot
              $corner={corner}
              aria-hidden="true"
              data-testid={`overlay-resize-corner-dot-${corner}`}
            />
          </div>
        )
      })}
    </>
  )
}
